import { useCallback, useEffect, useState } from 'react';

import { ChangeRequestResponse, changeLogGetEntityChangeLog } from '@pmp/api';

import { ChangeLogEntry, ChangeLogTarget, ChangeOrigin } from './interfaces';
import { parseFieldPath } from './fields';
import { getLoggedEntityTypes, getRowCollection, getSubTables } from './subTables';

/** The backend's largest page. */
const PAGE_SIZE = 500;

/** Column keys of the fields a change request touched, each once. */
export const toFieldKeys = (modifiedFields: string[] | undefined): string[] => [
  ...new Set(
    (modifiedFields ?? [])
      .map(parseFieldPath)
      .filter(path => !path.excluded)
      .map(path => path.field),
  ),
];

/** A change request filed in a collection names its fields under it, without the ones the collection hides. */
const toEntryFields = (target: ChangeLogTarget, changeRequest: ChangeRequestResponse): string[] => {
  const fields = toFieldKeys(changeRequest.modifiedFields);
  const collection = getRowCollection(target.entityType, changeRequest.actionEntityType?.key);
  if (!collection) return fields;

  const hidden = getSubTables(target.entityType)[collection]?.hiddenFields ?? [];

  return fields.filter(field => !hidden.includes(field)).map(field => `${collection}.${field}`);
};

const toEntry = (target: ChangeLogTarget, changeRequest: ChangeRequestResponse): ChangeLogEntry | null => {
  const actionId = changeRequest.actionId;
  if (!actionId) return null;

  return {
    id: actionId,
    changedBy: changeRequest.createdBy ?? '',
    changedAt: changeRequest.appliedAt ?? null,
    origin: { key: ChangeOrigin.PORTAL, value: ChangeOrigin.PORTAL },
    changeRequestId: actionId,
    fields: toEntryFields(target, changeRequest),
    entityType: changeRequest.actionEntityType?.key ?? null,
  };
};

/** Every applied change request that touched the record, newest applied first, read across all pages. */
export const useChangeLogEntries = (target: ChangeLogTarget | null) => {
  const [entries, setEntries] = useState<ChangeLogEntry[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (signal: AbortSignal) => {
      if (!target) return;
      setIsFetching(true);
      setFailed(false);

      try {
        const changeRequests: ChangeRequestResponse[] = [];
        let paginationToken: string | undefined;

        do {
          const { data } = await changeLogGetEntityChangeLog({
            query: { entityType: getLoggedEntityTypes(target.entityType), entityId: target.id, pageSize: PAGE_SIZE, paginationToken },
            signal,
          });
          if (signal.aborted) return;

          changeRequests.push(...data.items);
          paginationToken = data.pageOptions.paginationToken ?? undefined;
        } while (paginationToken);

        setEntries(changeRequests.map(changeRequest => toEntry(target, changeRequest)).filter((entry): entry is ChangeLogEntry => !!entry));
      } catch {
        if (signal.aborted) return;
        setEntries([]);
        setFailed(true);
      } finally {
        if (!signal.aborted) setIsFetching(false);
      }
    },
    [target],
  );

  useEffect(() => {
    if (!target) {
      setEntries([]);
      return;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [target, load]);

  return { entries, isFetching, failed };
};
